// Ship-from address book — addresses only. The sender name printed on the label
// comes from the store assignment below, so the form has no name field.
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { MapPin, Pencil, Plus, Star, Trash2 } from 'lucide-react'
import { Card } from '../../../components/ui/card'
import { API_BASE } from '../../../lib/api'
import { useT } from '../../../i18n'
import { shipFromLocationsI18n } from '../../../i18n/admin/ship-from-locations'
import {
  LOCATIONS_QUERY_KEY,
  STORE_ASSIGNMENTS_QUERY_KEY,
  fetchLocations,
  formatAddress,
  type ShipFromLocation,
} from './ship-from.lib'

const base = `${API_BASE}/admin/ship-from-locations`

type FormState = {
  line1: string
  line2: string
  city: string
  state: string
  postal_code: string
  phone: string
  is_default: boolean
}

const EMPTY_FORM: FormState = {
  line1: '',
  line2: '',
  city: '',
  state: '',
  postal_code: '',
  phone: '',
  is_default: false,
}

function toForm(row: ShipFromLocation): FormState {
  return {
    line1: row.address.line1 ?? '',
    line2: row.address.line2 ?? '',
    city: row.address.city ?? '',
    state: row.address.state ?? '',
    postal_code: row.address.postal_code ?? '',
    phone: row.address.phone ?? '',
    is_default: row.is_default,
  }
}

function toAddress(f: FormState) {
  return {
    line1: f.line1.trim(),
    line2: f.line2.trim() || null,
    city: f.city.trim(),
    state: f.state.trim(),
    postal_code: f.postal_code.trim(),
    country_code: 'US',
    phone: f.phone.trim() || null,
  }
}

function addressLabel(f: FormState): string {
  return [f.line1.trim(), [f.city.trim(), f.state.trim()].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(', ')
}

const buttonCls =
  'inline-flex min-h-8 items-center gap-1.5 rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-2.5 text-xs font-medium text-[var(--color-text)] hover:bg-[var(--color-surface-muted)] disabled:opacity-50'
const iconButtonCls =
  'inline-flex h-8 w-8 items-center justify-center rounded-md text-[var(--color-text-muted)] hover:bg-[var(--color-surface-muted)] hover:text-[var(--color-text)] disabled:opacity-50'

export function ShipFromAddressesSection() {
  const t = useT(shipFromLocationsI18n).addresses
  const qc = useQueryClient()
  const [form, setForm] = useState<FormState | null>(null)
  const [editingId, setEditingId] = useState<string | null>(null)
  const { data, isLoading, isError } = useQuery({
    queryKey: LOCATIONS_QUERY_KEY,
    queryFn: fetchLocations,
  })

  const invalidate = () => qc.invalidateQueries({ queryKey: LOCATIONS_QUERY_KEY })
  const close = () => {
    setForm(null)
    setEditingId(null)
  }

  const save = useMutation({
    mutationFn: async (f: FormState) => {
      const body = editingId
        ? { name: addressLabel(f), address: toAddress(f), is_default: f.is_default }
        : { address: toAddress(f), is_default: f.is_default }
      const res = await fetch(editingId ? `${base}/${editingId}` : base, {
        method: editingId ? 'PATCH' : 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      })
      if (!res.ok) throw new Error(`${res.status}`)
      return res.json()
    },
    onSuccess: () => {
      close()
      invalidate()
    },
  })

  const setDefault = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`${base}/${id}`, {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ is_default: true }),
      })
      if (!res.ok) throw new Error(`${res.status}`)
    },
    onSuccess: invalidate,
  })

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`${base}/${id}`, { method: 'DELETE' })
      if (!res.ok) throw new Error(`${res.status}`)
    },
    onSuccess: () => {
      invalidate()
      qc.invalidateQueries({ queryKey: STORE_ASSIGNMENTS_QUERY_KEY })
    },
  })

  const rows = data?.items ?? []

  return (
    <div className="space-y-3">
      {isLoading ? (
        <Card className="p-6 text-sm text-[var(--color-text-muted)]">{t.loading}</Card>
      ) : isError ? (
        <Card className="p-6 text-sm text-[var(--color-danger)]">
          {t.loadError}
        </Card>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {rows.map((row) => (
            <Card key={row.id} className="flex flex-col gap-3 p-4">
              <div className="flex items-start gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-[var(--color-surface-muted)] text-[var(--color-text-muted)]">
                  <MapPin className="h-4 w-4" aria-hidden />
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-[var(--color-text)]">{row.address.line1}</p>
                  {row.address.line2 && (
                    <p className="text-sm text-[var(--color-text)]">{row.address.line2}</p>
                  )}
                  <p className="text-sm text-[var(--color-text-muted)]">
                    {[row.address.city, row.address.state, row.address.postal_code]
                      .filter(Boolean)
                      .join(' ')}
                  </p>
                  {row.address.phone && (
                    <p className="text-xs text-[var(--color-text-muted)]">{row.address.phone}</p>
                  )}
                </div>
              </div>
              <div className="mt-auto flex items-center justify-between gap-2">
                {row.is_default ? (
                  <span className="inline-flex items-center gap-1 rounded-full border border-[var(--color-border)] px-2 py-0.5 text-[11px] font-medium text-[var(--color-text)]">
                    <Star className="h-3 w-3 text-[var(--color-signal)]" aria-hidden /> {t.default}
                  </span>
                ) : (
                  <button
                    type="button"
                    className={buttonCls}
                    disabled={setDefault.isPending}
                    onClick={() => setDefault.mutate(row.id)}
                  >
                    {t.makeDefault}
                  </button>
                )}
                <div className="flex items-center">
                  <button
                    type="button"
                    className={iconButtonCls}
                    aria-label={t.edit(formatAddress(row.address))}
                    onClick={() => {
                      setEditingId(row.id)
                      setForm(toForm(row))
                    }}
                  >
                    <Pencil className="h-4 w-4" aria-hidden />
                  </button>
                  <button
                    type="button"
                    className={iconButtonCls}
                    aria-label={t.delete(formatAddress(row.address))}
                    disabled={remove.isPending}
                    onClick={() => remove.mutate(row.id)}
                  >
                    <Trash2 className="h-4 w-4" aria-hidden />
                  </button>
                </div>
              </div>
            </Card>
          ))}
          {form === null && (
            <button
              type="button"
              onClick={() => {
                setEditingId(null)
                setForm({ ...EMPTY_FORM, is_default: rows.length === 0 })
              }}
              className="flex min-h-32 flex-col items-center justify-center gap-1.5 rounded-[var(--card-radius)] border border-dashed border-[var(--color-border-strong,var(--color-border))] text-sm font-medium text-[var(--color-text-muted)] hover:bg-[var(--color-surface)] hover:text-[var(--color-text)]"
            >
              <Plus className="h-4 w-4" aria-hidden />
              {t.add}
            </button>
          )}
        </div>
      )}

      {form !== null && (
        <AddressForm
          form={form}
          editing={editingId !== null}
          onChange={setForm}
          onCancel={close}
          onSave={() => save.mutate(form)}
          saving={save.isPending}
          error={save.isError}
        />
      )}
    </div>
  )
}

function AddressForm({
  form,
  editing,
  onChange,
  onCancel,
  onSave,
  saving,
  error,
}: {
  form: FormState
  editing: boolean
  onChange: (f: FormState) => void
  onCancel: () => void
  onSave: () => void
  saving: boolean
  error: boolean
}) {
  const t = useT(shipFromLocationsI18n).addresses
  const field = (key: keyof FormState, label: string, className = '') => (
    <label className={`flex flex-col gap-1 text-xs font-medium text-[var(--color-text-muted)] ${className}`}>
      {label}
      <input
        value={form[key] as string}
        onChange={(e) => onChange({ ...form, [key]: e.target.value })}
        className="min-h-9 rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-2.5 text-sm text-[var(--color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-info)]"
      />
    </label>
  )
  const valid =
    form.line1.trim() && form.city.trim() && form.state.trim() && form.postal_code.trim()

  return (
    <Card className="space-y-3 p-4">
      <p className="text-sm font-semibold text-[var(--color-text)]">
        {editing ? t.editTitle : t.newTitle}
      </p>
      <div className="grid gap-3 sm:grid-cols-4">
        {field('line1', t.street, 'sm:col-span-4')}
        {field('line2', t.suite, 'sm:col-span-4')}
        {field('city', t.city, 'sm:col-span-2')}
        {field('state', t.state)}
        {field('postal_code', t.zip)}
        {field('phone', t.phone, 'sm:col-span-2')}
      </div>
      <label className="flex items-center gap-2 text-sm text-[var(--color-text)]">
        <input
          type="checkbox"
          checked={form.is_default}
          onChange={(e) => onChange({ ...form, is_default: e.target.checked })}
        />
        {t.defaultCheckbox}
      </label>
      {error && (
        <p className="text-xs text-[var(--color-danger)]">
          {t.saveFailed}
        </p>
      )}
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} className={buttonCls}>
          {t.cancel}
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={saving || !valid}
          className="inline-flex min-h-8 items-center rounded-md bg-[var(--color-action)] px-3 text-xs font-medium text-[var(--color-action-foreground)] hover:bg-[var(--color-action-hover)] disabled:opacity-50"
        >
          {saving ? t.saving : t.save}
        </button>
      </div>
    </Card>
  )
}
